import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Compass, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const { lang, t, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password, rememberMe);
      toast.success(t("auth.welcome_back"));
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("auth.error_default"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ background: "#F9F6F0" }}>
      {/* Lang toggle top-right */}
      <div className="fixed top-4 right-4">
        <button
          onClick={toggleLang}
          className="text-xs px-2 py-1 border border-[#DCD4C6] text-[#9A8F86] hover:border-[#C17F59] hover:text-[#C17F59] transition-all"
          style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "2px", letterSpacing: "0.06em" }}
          data-testid="lang-toggle-btn"
        >
          {lang === "fr" ? "EN" : "FR"}
        </button>
      </div>
      <div className="w-full max-w-md animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex flex-col items-center gap-2" style={{ textDecoration: "none" }}>
            <div className="w-12 h-12 rounded-full border-2 border-[#C17F59] flex items-center justify-center">
              <Compass size={20} color="#C17F59" strokeWidth={1.5} />
            </div>
            <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontWeight: 600, fontSize: "1.6rem", color: "#2B2623" }}>
              Voyage-r
            </span>
          </Link>
          <h1
            className="mt-4 text-3xl"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
          >
            {t("auth.login_title")}
          </h1>
          <p className="text-sm text-[#5C554E] mt-1 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            {t("auth.login_subtitle")}
          </p>
        </div>

        <div className="card-surface">
          {error && (
            <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="login-error">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                {t("auth.email")}
              </label>
              <input
                type="email"
                className="input-field"
                placeholder={t("auth.email_placeholder")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                autoComplete="email"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                {t("auth.password")}
              </label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  className="input-field pr-8"
                  placeholder={t("auth.password_placeholder")}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#9A8F86] hover:text-[#5C554E]"
                  onClick={() => setShowPwd(!showPwd)}
                >
                  {showPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="btn-primary w-full mt-2"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? t("auth.login_loading") : t("auth.login_btn")}
            </button>

            {/* Rester connecté */}
            <label
              className="flex items-center gap-2 cursor-pointer select-none"
              data-testid="remember-me-label"
            >
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 accent-[#C17F59] cursor-pointer"
                data-testid="remember-me-checkbox"
              />
              <span
                className="text-sm text-[#5C554E]"
                style={{ fontFamily: "'Lora', Georgia, serif" }}
              >
                Rester connecté
              </span>
            </label>
          </form>
        </div>

        <p className="text-center text-sm text-[#5C554E] mt-6" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {t("auth.no_account")}{" "}
          <Link to="/register" className="text-[#C17F59] hover:text-[#A86A46] transition-colors" style={{ fontWeight: 500 }} data-testid="login-register-link">
            {t("auth.create_account")}
          </Link>
        </p>
      </div>
    </div>
  );
}
